/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";

import {
  Title,
  Select,
  GoBack,
  TextArea,
  InputField,
  MyMessage,
  MyActionButton,
} from "../../component/Component";
import { usePartnerContext } from "../../../../context/partner/PartnerContext";
import { defaultImage } from "../../../../middlewares/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../../middlewares/libraries/imageKit/ImageKitLibrary";
import { FOLDERS } from "../../../doseeData/utils/Utils";

const Add = () => {
  const { createOnePartner } = usePartnerContext();

  const [thumbnail, setThumbnail] = useState(defaultImage);
  const [thumbnailId, setThumbnailId] = useState("img_cdn_id");
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [title, setTitle] = useState(null);
  const [partnerStatus, setPartnerStatus] = useState(1);
  const [description, setDescription] = useState(null);

  const partnerRoutes = "/partner";

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
    setThumbnail(null);
    setThumbnailId(null);
    setTitle(null);
    setDescription(null);
  };

  const responses = (isError, message) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const addOnePartnerHandler = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      return responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  /**
   * @author Ntavigwa Bashombe
   * @param {*} thumbnail
   * @returns
   */
  const sendDBRequest = async () => {
    const data = {
      name: title,
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      status: partnerStatus,
    };

    const { status, message } = await createOnePartner(data);
    if (status === 200) {
      cleanFields();
      responses(false, message);
    } else {
      setLoading(false);
      responses(true, message);
    }
  };

  const hasLogo = !!thumbnailId;
  const hasName = !!title;

  return (
    // Preserve original height/scroll behavior to avoid overflow regressions
    <div className="p-4 h-full overflow-y-auto rounded-b-xl bg-gray-50">
      <div className="mx-auto w-full max-w-6xl">
        {/* Top row (no extra IDs near GoBack) */}
        <div className="mb-3 flex items-center justify-between">
          <GoBack props={{ route: partnerRoutes }} />
          <div className="hidden items-center gap-2 sm:flex">
            <StatusChip label="Logo" ok={hasLogo} />
            <StatusChip label="Name" ok={hasName} />
          </div>
        </div>

        <form onSubmit={addOnePartnerHandler} aria-busy={loading}>
          {/* Messages */}
          <div className="mb-3">
            <MyMessage
              error={error}
              message={message}
              success={success}
              onExpired={() => {
                setError(false);
                setSuccess(false);
              }}
              onClick={() => {
                setError(false);
                setSuccess(false);
                setErrorMessage("");
              }}
            />
          </div>

          {/* Header card */}
          <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-sm font-semibold tracking-tight text-gray-900 lg:text-base">
                  Add Partner
                </h1>
                <p className="mt-1 text-[11px] text-gray-500">
                  Upload a logo and provide a short, clear description.
                </p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-[11px] font-medium ring-1 ring-gray-200">
                <span
                  className={[
                    "h-1.5 w-1.5 rounded-full",
                    loading ? "bg-amber-500" : success ? "bg-emerald-500" : "bg-gray-300",
                  ].join(" ")}
                />
                {loading ? "Saving…" : success ? "Saved" : "Draft"}
              </span>
            </div>
          </div>

          {/* Brand Assets */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <Title props={{ title: "Brand Assets" }} />
              <span className="text-[11px] text-gray-500">
                JPEG/PNG • Square preferred
              </span>
            </div>
            <div className="p-4">
              <div className="rounded-xl border border-dashed border-gray-200 p-3">
                <ImageKitUpload
                  thumbnail={thumbnail}
                  thumbnailId={thumbnailId}
                  setThumbnail={setThumbnail}
                  setThumbnailId={setThumbnailId}
                  folder={FOLDERS.PARTNERS}
                />
              </div>
            </div>
          </section>

          {/* Partner Details */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b px-4 py-3">
              <Title props={{ title: "Partner Details" }} />
            </div>

            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-lg border bg-gray-50/60 p-3 hover:bg-gray-50">
                <InputField
                  props={{
                    id: "title",
                    title: "Name",
                    type: "text",
                    required: true,
                    value: title ? title : "",
                    onChange: (e) => {
                      setTitle(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
              </div>

              <div className="rounded-lg border bg-gray-50/60 p-3 hover:bg-gray-50">
                <Select
                  props={{
                    id: "event_status",
                    title: "Status",
                    required: true,
                    option: ["visible", "invisible"],
                    onChange: (e) => {
                      if (e.target.selectedIndex === 0) setPartnerStatus(1);
                      else setPartnerStatus(0);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
              </div>
            </div>

            <div className="p-4 pt-0">
              <div className="rounded-lg border bg-gray-50/60 p-3 hover:bg-gray-50">
                <TextArea
                  props={{
                    id: "description",
                    title: "Description",
                    type: "text",
                    required: true,
                    value: description ? description : "",
                    rows: 8,
                    onChange: (e) => {
                      setDescription(e.target.value);
                      setError(null);
                      setSuccess(null);
                    },
                  }}
                />
                <p className="mt-2 text-[11px] text-gray-500">
                  Aim for 120–160 characters for best readability.
                </p>
              </div>
            </div>
          </section>

          {/* Actions */}
          <div className="mt-4 flex items-center justify-end">
            <MyActionButton
              loading={loading}
              title="Add Partner"
              onClick={() => {
                setError(false);
                setSuccess(false);
                setErrorMessage(null);
              }}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default Add;

const StatusChip = ({ label, ok }) => (
  <span
    className={[
      "inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1",
      ok
        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
        : "bg-gray-50 text-gray-700 ring-gray-200",
    ].join(" ")}
  >
    <span
      className={["h-1.5 w-1.5 rounded-full", ok ? "bg-emerald-500" : "bg-gray-300"].join(" ")}
    />
    {label}
  </span>
);
